import { Box, Stack } from "@mui/material";
import { useCallback } from "react";
import { HdgTextInput } from "../../../../components/hdg-text-input";
import {
  BudgetRange,
  ProjectCategory,
  ProjectDuration,
  ProjectFile,
  ProjectStyles,
  SpecialRequirements,
  SquareFootage,
} from "../../../../__generated__/types";
import { DropdownSelect } from "../../../../components/dropdown-select";
import { RichTextEditor } from "../../../../components/rich-text-editor";
import {
  budgetRangeOptions,
  projectCategoryOptions,
  projectDurationOptions,
  projectStyleOptions,
  specialRequirementsOptions,
  squareFootageOptions,
} from "../../../../utils";

export const AddProjectForm = ({
  state,
  handleChange,
  errors,
}: {
  state: ProjectFile;
  handleChange: (key: keyof ProjectFile) => (value: string | string[]) => void;
  errors: Partial<Record<keyof ProjectFile, string>>;
}) => {
  const handleSpecialRequirementsChange = useCallback(
    (value: SpecialRequirements[]) => {
      const normalized = value.filter(
        (requirement) => requirement !== SpecialRequirements.NotApplicable
      );
      handleChange("specialRequirements")(normalized);
    },
    [handleChange]
  );

  return (
    <Box sx={{ maxWidth: "494px" }}>
      <Stack spacing={2.8}>
        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          spacing={2}
        >
          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <HdgTextInput
              label="Name"
              value={state?.name as string}
              onChange={handleChange("name")}
              isValid={!!!errors.name}
              helperText={errors.name}
            />
          </Box>

          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <DropdownSelect
              value={state.projectCategory as ProjectCategory}
              label="Project Type"
              onChange={handleChange("projectCategory")}
              isValid={!!!errors.projectCategory}
              helperText={errors.projectCategory}
              options={projectCategoryOptions}
            />
          </Box>
        </Stack>
        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          spacing={2}
        >
          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <DropdownSelect<ProjectStyles>
              label="Style"
              value={state.style as ProjectStyles}
              onChange={handleChange("style")}
              isValid={!!!errors.style}
              helperText={errors.style}
              options={projectStyleOptions}
            />
          </Box>

          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <DropdownSelect<SpecialRequirements[]>
              label="Special Requirements"
              value={state.specialRequirements as SpecialRequirements[]}
              onChange={handleSpecialRequirementsChange}
              isValid={!!!errors.specialRequirements}
              helperText={errors.specialRequirements}
              options={specialRequirementsOptions}
              multiple
            />
          </Box>
        </Stack>

        <Stack
          direction={{
            xs: "column",
            lg: "row",
          }}
          spacing={2}
        >
          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <DropdownSelect<BudgetRange>
              label="Budget Range"
              value={state.budgetRange as BudgetRange}
              onChange={handleChange("budgetRange")}
              isValid={!!!errors.budgetRange}
              helperText={errors.budgetRange}
              options={budgetRangeOptions}
            />
          </Box>

          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <DropdownSelect<SquareFootage>
              label="Sq Footage"
              value={state.squareFootage as SquareFootage}
              onChange={handleChange("squareFootage")}
              isValid={!!!errors.squareFootage}
              helperText={errors.squareFootage}
              options={squareFootageOptions}
            />
          </Box>

          <Box sx={{ width: "100%", maxWidth: "494px" }}>
            <DropdownSelect<ProjectDuration>
              label="Project Duration"
              value={state.projectDuration as ProjectDuration}
              onChange={handleChange("projectDuration")}
              isValid={!!!errors.projectDuration}
              helperText={errors.projectDuration}
              options={projectDurationOptions}
            />
          </Box>
        </Stack>

        <Box sx={{ width: "100%", maxWidth: "494px" }}>
          <RichTextEditor
            label="Description"
            value={(state.description as string) || ""}
            onChange={(html) => handleChange("description")(html)}
            maxChars={1000}
            helperText={errors.description}
            error={Boolean(errors.description)}
          />
        </Box>
      </Stack>
    </Box>
  );
};
